import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sparkles, ArrowRight, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useShowcase, type ShowcaseItem } from '@/hooks/useShowcase';
import { SHOWCASE_EXAMPLES } from '@/data/showcaseExamples';

// Polished stand-in titles for curated clips that come back with a bland or
// missing title ("Custom", "Untitled", etc.). Picked deterministically by id so
// a given clip always shows the same one.
const FALLBACK_TITLES = [
  'Golden Hour',
  'City in Motion',
  'Concrete Dreams',
  'After the Rain',
  'Neon Nights',
  'First Light',
  'Quiet Mornings',
  'Midnight Drive',
  'Open Road',
  'Still Frames',
  'Paper Skies',
  'Slow Burn',
  'Northern Light',
  'Soft Focus',
  'The Long Way',
  'Last Summer',
];

const GENERIC_TITLES = new Set(['custom', 'untitled', 'new content', 'reel', '']);

function hashId(id: ShowcaseItem['id']): number {
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

function displayTitle(item: ShowcaseItem): string {
  const title = (item.title || '').trim();
  if (title && !GENERIC_TITLES.has(title.toLowerCase())) return title;
  return FALLBACK_TITLES[hashId(item.id) % FALLBACK_TITLES.length];
}

function ShowcaseCard({ item }: { item: ShowcaseItem }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [inView, setInView] = useState(false);

  // Only play the clips that are actually on screen — keeps a big grid smooth.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="group relative aspect-[9/16] overflow-hidden rounded-2xl bg-neutral-900 shadow-lg ring-1 ring-white/10 transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl">
      {item.video_url ? (
        <video
          ref={ref}
          src={item.video_url}
          poster={item.thumbnail_url || undefined}
          muted
          loop
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-neutral-800 text-neutral-500">
          <Play className="h-8 w-8" />
        </div>
      )}

      {/* Model badge */}
      {item.model && (
        <div className="absolute right-2 top-2 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white/90 backdrop-blur-sm">
          {item.model}
        </div>
      )}

      {/* Gradient + title + prompt */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-3 pt-12">
        <p className="line-clamp-1 text-sm font-semibold text-white drop-shadow">
          {displayTitle(item)}
        </p>
        {item.prompt && (
          <p className="mt-1 line-clamp-2 font-mono text-[11px] leading-snug text-white/70">
            {item.prompt}
          </p>
        )}
      </div>

      {/* Soft play hint until it's playing */}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300',
          inView ? 'opacity-0' : 'opacity-100',
        )}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm">
          <Play className="h-5 w-5 fill-white text-white" />
        </div>
      </div>
    </div>
  );
}

export function ShowcasePage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useShowcase();
  // Curated prod feed first, then the always-on landing examples. Dedupe by id
  // so a curated id can't collide with an example.
  const feed = data?.items ?? [];
  const seen = new Set(feed.map(i => String(i.id)));
  const items: ShowcaseItem[] = [
    ...feed,
    ...SHOWCASE_EXAMPLES.filter(e => !seen.has(String(e.id))),
  ];

  return (
    <div className="min-h-screen bg-neutral-950">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-[40rem] -translate-x-1/2 rounded-full bg-primary/30 blur-[120px]" />
        <div className="container relative mx-auto max-w-5xl px-4 py-16 text-center md:py-24">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-white/80">
            <Sparkles className="h-3.5 w-3.5" /> Made with SmartReel
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white md:text-6xl">
            Real videos, made from a single prompt
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/60 md:text-lg">
            Every clip below was generated on SmartReel — no editing, no timeline. Just an
            idea in, a finished reel out.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={() => navigate('/auth')}
              className="h-11 bg-primary hover:bg-primary/90"
            >
              Create yours <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="container mx-auto max-w-6xl px-4 py-10 md:py-14">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {items.map(item => (
            <ShowcaseCard key={item.id} item={item} />
          ))}
        </div>

        {isLoading && (
          <div className="mt-8 flex items-center justify-center text-sm text-white/50">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading more...
          </div>
        )}
        {isError && (
          <div className="mt-8 flex items-center justify-center gap-3 text-sm text-white/50">
            <span>Couldn't load the latest reels.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
